import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { navigateWithFrom } from '../utils/navigationUtils';
import { sanitizeString, sanitizeSearchInput } from '../utils/security';
import { safeApiRequest } from '../services/apiClient';
import { fetchHakedisByRandevuId } from '../services/hakedisService';
import HakedisDetayModal from '../components/HakedisDetayModal';
import ServisKaydiDetayModal from '../components/ServisKaydiDetayModal';
import TableListFooter from '../components/TableListFooter';
import './CompletedAppointmentsPage.css';

const safeText = (value) => sanitizeString(value ?? '');

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (error?.status === 404) return 'API endpoint bulunamadı.';
  if (error?.status === 401) return 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.';
  if (error?.status === 403) return 'Bu işlem için yetkiniz yok.';
  let message = error?.message || error?.data?.mesaj || error?.data?.message || error?.data?.error || error?.data?.hata || fallback;
  if (message === 'Failed to fetch') return 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
  return message;
};

// Tamamlanan randevuları getir
// Admin/mod için backend'de teknikServisId/teknisyenId zorunlu değil
const fetchCompletedAppointments = async ({ page = 1, limit = 50, arama } = {}) => {
  const queryParams = {
    sayfa: page,
    limit: limit,
    durum: 'Randevu Tamamlandı',
    aktif: 'true',
    siralama: 'desc',
  };
  
  // Arama parametresi varsa ekle (randevu numarası için)
  if (arama && arama.trim().length > 0) {
    queryParams.arama = arama.trim();
  }
  
  return safeApiRequest('/api/appointments', {
    method: 'GET',
    query: queryParams,
    skipCsrf: true,
  });
};

const formatDateTime = (dateValue) => {
  if (!dateValue) return '—';
  try {
    let date;
    if (dateValue && typeof dateValue === 'object' && dateValue.$date) {
      date = new Date(dateValue.$date);
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      return '—';
    }
    if (!date || isNaN(date.getTime())) return '—';
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return '—';
  }
};

/** randevuTarihi (gün) + randevuSaati (HH:mm) birleşik gösterim — tarih alanı gece yarısı tutulur. */
const formatRandevuTarihSaati = (appointment) => {
  if (!appointment) return '—';
  const { randevuTarihi, randevuSaati } = appointment;
  if (randevuTarihi) {
    try {
      const date = new Date(randevuTarihi);
      if (!isNaN(date.getTime())) {
        const dateStr = date.toLocaleDateString('tr-TR', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
        return randevuSaati ? `${dateStr} ${randevuSaati}` : dateStr;
      }
    } catch {
      // fall through
    }
  }
  return formatDateTime(appointment.tamamlanmaBilgileri?.tamamlanmaTarihi);
};

const getIslemTipiMeta = (islemTipi) => {
  const t = String(islemTipi || '').trim().toLowerCase();
  if (t.includes('bakım') || t.includes('bakim')) return { label: 'Bakım', token: 'bakim' };
  if (t.includes('kurulum')) return { label: 'Kurulum', token: 'kurulum' };
  if (t.includes('arıza') || t.includes('ariza')) return { label: 'Arıza', token: 'ariza' };
  if (islemTipi) return { label: String(islemTipi), token: 'default' };
  return { label: '—', token: 'default' };
};

const getGarantiMeta = (appointment, islemTipi) => {
  const t = String(islemTipi || '').trim().toLowerCase();
  if (t.includes('bakım') || t.includes('bakim')) {
    return { label: 'Garanti Harici', token: 'harici' };
  }
  if (appointment?.garantiDahilinde === true) {
    return { label: 'Garanti Dahilinde', token: 'dahil' };
  }
  if (appointment?.garantiDahilinde === false) {
    return { label: 'Garanti Harici', token: 'harici' };
  }
  if (t.includes('kurulum')) {
    return { label: 'Garanti Dahilinde', token: 'dahil' };
  }
  return { label: '—', token: 'unknown' };
};

const getTeknikServisAdi = (appointment) => {
  if (appointment?.teknikServis?.teknikServisAdi) return appointment.teknikServis.teknikServisAdi;
  const ts = appointment?.teknikServis || appointment?.teknikServisId;
  if (ts && typeof ts === 'object') {
    const ad = ts.ad || ts.name || '';
    const kod = ts.kod;
    if (ad && kod) return `${ad} (${kod})`;
    return ad || '—';
  }
  return '—';
};

const CompletedAppointmentsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(50);
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({ search: '' });
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    totalPages: 1,
  });
  const [hakedisMap, setHakedisMap] = useState({}); // randevuId -> hakediş
  const [hakedisDetailOpen, setHakedisDetailOpen] = useState(false);
  const [selectedHakedis, setSelectedHakedis] = useState(null);
  const [servisKaydiDetailOpen, setServisKaydiDetailOpen] = useState(false);
  const [selectedServisKaydiId, setSelectedServisKaydiId] = useState(null);
  const [selectedKaynakRandevuId, setSelectedKaynakRandevuId] = useState(null);

  const sanitizedSearchValue = useMemo(() => {
    const sanitized = sanitizeSearchInput(filters.search);
    return sanitized && sanitized.trim().length > 0 ? sanitized : undefined;
  }, [filters.search]);

  const loadAppointments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchCompletedAppointments({
        page: currentPage,
        limit: rowsPerPage,
        arama: sanitizedSearchValue,
      });
      
      const list = response?.veri?.randevular || response?.veri || [];
      const pagination = response?.veri?.sayfalama || {};
      
      setAppointments(list);
      setPaginationMeta({
        total: pagination.toplam || list.length,
        totalPages: pagination.toplamSayfa || Math.ceil(list.length / rowsPerPage) || 1,
      });
    } catch (err) {
      setError(getErrorMessage(err, 'Tamamlanan randevular yüklenirken hata oluştu'));
      setAppointments([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  }, [currentPage, rowsPerPage, sanitizedSearchValue]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Hakediş bilgilerini randevular için yükle
  useEffect(() => {
    if (!appointments || appointments.length === 0) {
      setHakedisMap({});
      return;
    }
    let cancelled = false;
    const loadHakedisler = async () => {
      try {
        const results = await Promise.allSettled(
          appointments.map((apt) => {
            const randevuId = apt.id || apt._id;
            return randevuId ? fetchHakedisByRandevuId(randevuId) : Promise.resolve({ veri: { hakedis: null } });
          })
        );
        if (cancelled) return;
        const map = {};
        results.forEach((result, idx) => {
          const apt = appointments[idx];
          const randevuId = apt?.id || apt?._id;
          if (!randevuId) return;
          if (result.status === 'fulfilled' && result.value?.veri?.hakedis) {
            map[randevuId] = result.value.veri.hakedis;
          }
        });
        setHakedisMap(map);
      } catch (e) {
        if (!cancelled) setHakedisMap({});
      }
    };
    loadHakedisler();
    return () => { cancelled = true; };
  }, [appointments]);

  const handleOpenHakedisDetail = (hakedis) => {
    if (!hakedis) return;
    setSelectedHakedis(hakedis);
    setHakedisDetailOpen(true);
  };

  const handleOpenServisKaydiDetail = (appointment) => {
    const servisKaydiId = appointment.servisKaydiId || appointment.servisKaydi?.id || appointment.servisKaydi?._id;
    const randevuId = appointment.id || appointment._id;
    if (!servisKaydiId) {
      if (window.showNotification) {
        window.showNotification('error', 'Servis kaydı bilgisi bulunamadı');
      }
      return;
    }
    setSelectedServisKaydiId(servisKaydiId);
    setSelectedKaynakRandevuId(randevuId);
    setServisKaydiDetailOpen(true);
  };

  const handleSearchChange = (event) => {
    const value = sanitizeSearchInput(event.target.value, { trimEdges: false });
    setSearchInput(value);
  };

  // Debounce için search input
  useEffect(() => {
    // İlk yüklemede veya boş değerde spinner gösterme
    if (searchInput.trim().length === 0) {
      setSearchLoading(false);
      setFilters((prev) => ({ ...prev, search: '' }));
      return;
    }

    const timeoutId = setTimeout(() => {
      setSearchLoading(true);
      setFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1); // Arama yapıldığında sayfayı 1'e sıfırla
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const handleAppointmentDetail = (appointment) => {
    const randevuId = appointment.id || appointment._id;
    const servisKaydiId = appointment.servisKaydiId || appointment.servisKaydi?.id || appointment.servisKaydi?._id;
    
    if (!servisKaydiId) {
      if (window.showNotification) {
        window.showNotification('error', 'Servis kaydı ID bulunamadı');
      }
      return;
    }
    
    navigateWithFrom(navigate, `/service-records/${servisKaydiId}/appointments/${randevuId}`, location);
  };

  const renderTableBody = () => {
    if (loading && appointments.length === 0) {
      return (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Tamamlanan randevular yükleniyor</div>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      );
    }

    if (error) {
      return <div className="error-state">{safeText(error)}</div>;
    }

    if (appointments.length === 0) {
      return (
        <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
          <i className="fas fa-calendar-check" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
          <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Tamamlanan randevu bulunamadı</div>
        </div>
      );
    }

    return (
      <>
        <div className="table-container cap-table-wrap">
          <table className="admins-table">
            <thead>
              <tr>
                <th>Randevu No</th>
                <th>Servis Kaydı</th>
                <th>Müşteri</th>
                <th>İşlem Tipi</th>
                <th>Garanti</th>
                <th>Teknik Servis</th>
                <th>Randevu Tarihi</th>
                <th className="islemler-sutunu" style={{ textAlign: 'center' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {appointments.map((appointment) => {
                const randevuId = appointment.id || appointment._id;
                const randevuNumarasi = appointment.randevuNumarasi || '—';
                const hakedis = hakedisMap[randevuId];
                const serviceRecord =
                  appointment.servisKaydi
                  || (typeof appointment.servisKaydiId === 'object' ? appointment.servisKaydiId : null)
                  || {};
                const servisKaydiId =
                  appointment.servisKaydiId && typeof appointment.servisKaydiId !== 'object'
                    ? appointment.servisKaydiId
                    : serviceRecord?.id || serviceRecord?._id;
                const customer = serviceRecord.musteri || serviceRecord.musteriId || {};
                const customerName =
                  `${customer.adi || customer.name || ''} ${customer.soyadi || customer.surname || ''}`.trim() || '—';
                const islemTipi = serviceRecord.islemTipi || appointment.islemTipi;
                const islemMeta = getIslemTipiMeta(islemTipi);
                const garantiMeta = getGarantiMeta(appointment, islemTipi);
                const servisNumara = serviceRecord.numara || '—';
                const teknikServisAdi = getTeknikServisAdi(appointment);

                return (
                  <tr key={randevuId}>
                    <td>
                      <span className="cap-randevu-no">{randevuNumarasi}</span>
                    </td>
                    <td>
                      {servisKaydiId && servisNumara !== '—' ? (
                        <button
                          type="button"
                          className="cap-servis-link"
                          onClick={() => navigateWithFrom(
                            navigate,
                            `/service-records/${servisKaydiId}/update`,
                            location,
                          )}
                          title="Servis kaydına git"
                        >
                          {servisNumara}
                        </button>
                      ) : (
                        <span className="cap-muted">{servisNumara}</span>
                      )}
                    </td>
                    <td>{customerName}</td>
                    <td>
                      <span className={`cap-islem-badge cap-islem-badge--${islemMeta.token}`}>
                        {islemMeta.label}
                      </span>
                    </td>
                    <td>
                      <span className={`cap-garanti-badge cap-garanti-badge--${garantiMeta.token}`}>
                        {garantiMeta.label}
                      </span>
                    </td>
                    <td>{teknikServisAdi}</td>
                    <td>{formatRandevuTarihSaati(appointment)}</td>
                    <td className="table-actions islemler-sutunu" style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="category-action-btn edit-btn icon-only-btn"
                          onClick={() => handleOpenServisKaydiDetail(appointment)}
                          title="Servis Kaydı"
                          aria-label="Servis kaydı"
                        >
                          <i className="fas fa-clipboard-list"></i>
                        </button>
                        {hakedis && (
                          <button
                            type="button"
                            className="category-action-btn hakedis-btn icon-only-btn"
                            onClick={() => handleOpenHakedisDetail(hakedis)}
                            title="Hakedişe Git"
                            aria-label="Hakedişe git"
                          >
                            <i className="fas fa-file-invoice-dollar"></i>
                          </button>
                        )}
                        <button
                          type="button"
                          className="category-action-btn edit-btn icon-only-btn"
                          onClick={() => handleAppointmentDetail(appointment)}
                          title="Randevu Detayı"
                          aria-label="Randevu detayı"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <TableListFooter
          totalRecords={paginationMeta.total}
          currentPage={currentPage}
          totalPages={paginationMeta.totalPages || 1}
          onPageChange={setCurrentPage}
          loading={loading}
          embedded
        />
      </>
    );
  };

  return (
    <main className="main-content completed-appointments">
      <div className="content-header">
        <h1>Randevular</h1>
      </div>

      <div className="admin-finance-toolbar">
        <div className="admin-finance-search-row">
          <div className="search-box admin-finance-search-box">
            <i className="fas fa-search" aria-hidden="true"></i>
            <input
              type="text"
              placeholder="Randevu numarası ile ara…"
              value={searchInput}
              onChange={handleSearchChange}
              maxLength={100}
              autoComplete="off"
              aria-label="Randevu ara"
            />
            {searchLoading && searchInput.trim().length > 0 && (
              <i className="fas fa-spinner fa-spin admin-finance-search-spinner" aria-hidden="true"></i>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        {renderTableBody()}
      </div>

      {hakedisDetailOpen && selectedHakedis && (
        <HakedisDetayModal
          hakedis={selectedHakedis}
          onClose={() => {
            setHakedisDetailOpen(false);
            setSelectedHakedis(null);
          }}
          onRefresh={loadAppointments}
        />
      )}

      {servisKaydiDetailOpen && selectedServisKaydiId && (
        <ServisKaydiDetayModal
          servisKaydiId={selectedServisKaydiId}
          kaynakRandevuId={selectedKaynakRandevuId}
          onClose={() => {
            setServisKaydiDetailOpen(false);
            setSelectedServisKaydiId(null);
            setSelectedKaynakRandevuId(null);
          }}
        />
      )}
    </main>
  );
};

export default CompletedAppointmentsPage;
